import { Form, Input, Spin, Upload, message } from "antd";
import React, { useEffect, useState } from "react";
import adminServices from "../../services/httpServices";
import TextArea from "antd/es/input/TextArea";
import { UploadOutlined } from "@ant-design/icons";
import NotificationService from "../../NotificationService";

const EditBoard = ({ id, setIsEditModalOpen }) => {
  const [form] = Form.useForm();
  const [isloading, setIsloading] = useState(false);
  const [imagePath, setImagePath] = useState("");
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");
  const [fileList, setFileList] = useState([]);
  const [uploadedimage, setUploadedImage] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [boardMeta, setBoardMeta] = useState({});

  // ── Fetch existing member data ─────────────────────────────────────────────
  const fetchMember = async () => {
    if (!id) return;
    setIsloading(true);
    try {
      const res = await adminServices.BoardById(id);
      if (res?.data) {
        const d = res.data;
        form.setFieldsValue({
          title:       d.title,
          name:        d.name,
          description: d.description,
        });
        // Pre-populate image preview with existing image
        setImagePath(d.imgUrl || "");
        setImagePreviewUrl(d.imgUrl || "");
        setBoardMeta({
          id:          d.id,
          isActive:    d.isActive,
          categoryId:  d.categoryId,
          teamType:    d.teamType,
          dateCreated: d.dateCreated,
        });
      }
    } catch (error) {
      NotificationService.show(error.message, "error");
    } finally {
      setIsloading(false);
    }
  };

  // ── Image upload ───────────────────────────────────────────────────────────
  const uploadImage = async (file) => {
    if (!file) return imagePath;
    setImageUploading(true);
    try {
      const data = new FormData();
      data.append("File", file);

      const res = await adminServices.fileUpload(data);
      const path = res?.data?.path || res?.data?.data?.path || res?.path || "";

      if (!path) {
        message.error("Upload succeeded but no image URL was returned.");
        return imagePath;
      }

      // Only update imagePath (used for API submission), NOT imagePreviewUrl
      setImagePath(path);
      message.success("Image uploaded");
      return path;
    } catch (error) {
      message.error("Image upload failed: " + (error.message || "Unknown error"));
      return imagePath;
    } finally {
      setImageUploading(false);
    }
  };

  const handleImageChange = async (info) => {
    const file = info.file.originFileObj || info.file;
    if (!file) return;

    // Show local preview immediately and keep it — don't overwrite with Cloudinary URL
    const reader = new FileReader();
    reader.onload = (e) => setImagePreviewUrl(e.target.result);
    reader.readAsDataURL(file);

    setUploadedImage(file);
    await uploadImage(file);
  };

  const dummyRequest = ({ onSuccess }) => setTimeout(() => onSuccess("ok"), 0);

  // ── Submit ─────────────────────────────────────────────────────────────────
  const editHandler = async () => {
    try {
      await form.validateFields();
    } catch {
      return;
    }

    const currentValues = form.getFieldsValue();
    delete currentValues.photo55;

    // Upload new image if one was selected
    const finalImagePath = uploadedimage
      ? await uploadImage(uploadedimage)
      : imagePath;

    if (!finalImagePath) {
      message.warning("Please upload an image.");
      return;
    }

    setIsloading(true);
    const payload = {
      id:          boardMeta.id,
      title:       currentValues.title,
      name:        currentValues.name,
      description: currentValues.description,
      imgUrl:      finalImagePath,
      isActive:    boardMeta.isActive,
      categoryId:  boardMeta.categoryId,
      teamType:    boardMeta.teamType,
      dateCreated: boardMeta.dateCreated,
    };

    try {
      await adminServices.EditBoardById(parseInt(id), payload);
      message.success("Member updated successfully");
      setIsEditModalOpen(false);
    } catch (error) {
      NotificationService.show(error.message, "error");
    } finally {
      setIsloading(false);
    }
  };

  useEffect(() => {
    fetchMember();
  }, [id]);

  return (
    <Spin
      spinning={isloading || imageUploading}
      tip={imageUploading ? "Uploading image..." : "Saving changes..."}
    >
      <Form
        form={form}
        layout="vertical"
        className="pt-2"
        onFinish={editHandler}
      >
        {/* Image section */}
        <Form.Item
          label={
            <span className="font-semibold text-gray-700">Staff Image</span>
          }
          name="photo55"
        >
          <div className="flex items-start gap-4">
            {/* Preview */}
            <div
              style={{
                width: 96,
                height: 96,
                borderRadius: 12,
                border: "2px dashed #d1d5db",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#f9fafb",
                flexShrink: 0,
              }}
            >
              {imagePreviewUrl ? (
                <img
                  src={imagePreviewUrl}
                  alt="preview"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              ) : (
                <div style={{ textAlign: "center", padding: 8 }}>
                  <div style={{ fontSize: 24, color: "#d1d5db", marginBottom: 4 }}>📷</div>
                  <span style={{ fontSize: 11, color: "#9ca3af" }}>No image</span>
                </div>
              )}
            </div>

            <div className="flex-1">
              <Upload
                onChange={handleImageChange}
                customRequest={dummyRequest}
                maxCount={1}
                accept=".png,.jpg,.jpeg,.webp"
                showUploadList={false}
                fileList={fileList}
              >
                <button
                  type="button"
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-xl text-sm text-gray-700 hover:border-[#001529] hover:text-[#001529] transition-colors bg-white"
                >
                  <UploadOutlined />
                  {imageUploading ? "Uploading..." : "Change Image"}
                </button>
              </Upload>
              <p className="text-xs text-gray-400 mt-2">PNG, JPG, WEBP · Max 5MB</p>
              {imagePath && !imageUploading && (
                <p className="text-xs text-green-600 mt-1">✅ Image ready</p>
              )}
            </div>
          </div>
        </Form.Item>

        <Form.Item
          name="title"
          label={<span className="font-semibold text-gray-700">Title</span>}
          rules={[{ required: true, message: "Title is required" }]}
        >
          <Input placeholder="Enter title" className="rounded-xl h-10" />
        </Form.Item>

        <Form.Item
          name="name"
          label={
            <span className="font-semibold text-gray-700">Full Name</span>
          }
          rules={[{ required: true, message: "Name is required" }]}
        >
          <Input placeholder="Enter full name" className="rounded-xl h-10" />
        </Form.Item>

        <Form.Item
          name="description"
          label={
            <span className="font-semibold text-gray-700">Description</span>
          }
          rules={[{ required: true, message: "Description is required" }]}
        >
          <TextArea
            placeholder="Enter description"
            rows={4}
            className="rounded-xl"
          />
        </Form.Item>

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={() => setIsEditModalOpen(false)}
            className="flex-1 py-2.5 border border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={imageUploading}
            className="flex-1 py-2.5 bg-[#001529] text-white font-semibold rounded-xl hover:bg-[#002a50] transition-colors disabled:opacity-60"
          >
            Save Changes
          </button>
        </div>
      </Form>
    </Spin>
  );
};

export default EditBoard;